import type { FamilyPlanDetails } from '~/models/cellphone_page/FamilyPlanDetails.model';
import type {
  AggregatedBundlePriceDto,
  AggregatedPaymentDto,
} from '~/models/ListingProduct.api.model';
import type { PriceModalPeriodTotalFromBackend } from '~/models/pricing/PriceDetailsModalTypes.model';

const INFINITY = 99999;

function pickOfferValidities(
  bundlePrice: AggregatedBundlePriceDto | undefined,
  isFamilySet: boolean,
  familyDetails: FamilyPlanDetails | undefined,
): number[] {
  const offerValidities: number[] = [];

  if (bundlePrice?.Plan?.OfferValidityInMonthsDiscount) {
    offerValidities.push(bundlePrice.Plan.OfferValidityInMonthsDiscount);
  }

  if (
    isFamilySet &&
    familyDetails?.isNewCustomer === true &&
    familyDetails?.familyPlanSummaryItems?.length
  ) {
    for (const item of familyDetails.familyPlanSummaryItems) {
      const validity = item.Plan?.OfferValidityInMonthsDiscount;
      if (validity) {
        offerValidities.push(validity);
      }
    }
  }

  return offerValidities;
}

function resolveIntermediateTotal(
  isFamilySet: boolean,
  familyDetails: FamilyPlanDetails | undefined,
  payment: AggregatedPaymentDto | undefined,
  lowestOffer: number | undefined,
): number | null {
  if (!payment) {
    return null;
  }

  if (isFamilySet && familyDetails?.isNewCustomer === false) {
    if (familyDetails?.numberOfConnections === 1) {
      return payment.IntermediateMonthlyPrice || 0;
    }
    if (
      familyDetails?.numberOfConnections &&
      familyDetails.numberOfConnections > 1
    ) {
      const hardwarePrice = payment.DownPayment;
      const familyRegularMonthlyPrice =
        familyDetails.familyPlansTotalRegularMonthlyFee || 0;
      return familyRegularMonthlyPrice + hardwarePrice;
    }
  }

  if (isFamilySet && familyDetails?.isNewCustomer === true) {
    const intermediateMonthlyPriceForBundle =
      payment.IntermediateMonthlyPrice || payment.MonthlyPrice || 0;
    const intermediateMonthlyPriceForFamily =
      familyDetails.familyPlansTotalIntermediateMonthlyFee || 0;

    let adjustmentByDownPayment = 0;
    if (payment.Time === 12 && payment.Time <= (lowestOffer || 0)) {
      adjustmentByDownPayment = payment.DownPayment;
    }

    let mainPlanPrice = 0;
    if (
      familyDetails.familyPlanSummaryItems?.length &&
      familyDetails.parentPlanBundlePriceId
    ) {
      const familyBundle = familyDetails.familyPlanSummaryItems.find(
        (item) => item.BundlePriceId === familyDetails.parentPlanBundlePriceId,
      );
      mainPlanPrice = pdp_getLowestMainPlanPrice(
        payment.IntermediateMonthlyPrice,
        familyBundle?.Plan?.RegularMonthlyFee,
        familyBundle?.Plan?.MonthlyFee,
        payment.DownPayment,
      );
    }

    const result =
      intermediateMonthlyPriceForBundle +
      intermediateMonthlyPriceForFamily -
      mainPlanPrice -
      adjustmentByDownPayment;

    if (result <= 0) {
      return null;
    }
    return result;
  }

  return payment.IntermediateMonthlyPrice || null;
}

function selectPlanFees(
  isFamilySet: boolean,
  familyDetails: FamilyPlanDetails | undefined,
  planMonthlyFee: number | undefined,
  planRegularFee: number | undefined,
): { monthlyFee: number; regularFee: number } {
  if (isFamilySet && familyDetails?.familyPlansTotalMonthlyFee) {
    return {
      monthlyFee: familyDetails.familyPlansTotalMonthlyFee || 0,
      regularFee: familyDetails.familyPlansTotalRegularMonthlyFee || 0,
    };
  }
  return {
    monthlyFee: planMonthlyFee || 0,
    regularFee: planRegularFee || 0,
  };
}

function shouldShowIntermediate(
  intermediateTotal: number | null,
  lowestOffer: number | undefined,
  highestOffer: number | undefined,
  partialPaymentTime: number,
): boolean {
  if (intermediateTotal === null) {
    return false;
  }

  if (!lowestOffer) {
    return false;
  }

  if (
    lowestOffer === partialPaymentTime &&
    highestOffer === partialPaymentTime
  ) {
    return false;
  }

  if (lowestOffer === highestOffer && partialPaymentTime > highestOffer) {
    return false;
  }

  return true;
}

function shouldShowSecondIntermediate(
  intermediateTotal: number | null,
  lowestOffer: number | undefined,
  highestOffer: number | undefined,
  partialPaymentTime: number,
  isFamilySet: boolean,
): boolean {
  if (intermediateTotal === null) {
    return false;
  }

  if (!lowestOffer || !highestOffer) {
    return false;
  }

  if (
    partialPaymentTime === Math.max(0, highestOffer) &&
    partialPaymentTime === Math.max(0, lowestOffer)
  ) {
    return false;
  }

  if (lowestOffer === highestOffer && partialPaymentTime <= highestOffer) {
    return false;
  }

  if (
    partialPaymentTime <= highestOffer &&
    isFamilySet &&
    partialPaymentTime <= lowestOffer
  ) {
    return false;
  }

  if (highestOffer === partialPaymentTime) {
    return false;
  }

  return true;
}

export type PdpPeriodResult = {
  periods: PriceModalPeriodTotalFromBackend[];
  thereafterTotal: number;
};

export type PdpPeriodInput = {
  payment: AggregatedPaymentDto | undefined;
  bundlePrice: AggregatedBundlePriceDto | undefined;
  familyDetails: FamilyPlanDetails | undefined;
  isFamilySet: boolean;
  discountedTotal: number;
  regularTotal: number;
};

export function buildPdpPeriodTotals(
  input: PdpPeriodInput,
): PdpPeriodResult | undefined {
  const { payment, bundlePrice, familyDetails, isFamilySet } = input;
  if (!payment || payment.Time === 1) {
    return undefined;
  }

  const offerValidities = pickOfferValidities(
    bundlePrice,
    isFamilySet,
    familyDetails,
  );

  const lowestOffer =
    offerValidities.length > 0 ? Math.min(...offerValidities) : undefined;
  const highestOffer =
    offerValidities.length > 0 ? Math.max(...offerValidities) : undefined;

  const discountedTotal = input.discountedTotal;
  const regularTotal = input.regularTotal;
  const intermediateTotal = resolveIntermediateTotal(
    isFamilySet,
    familyDetails,
    payment,
    lowestOffer,
  );

  const { monthlyFee: planFee, regularFee: planRegularFee } = selectPlanFees(
    isFamilySet,
    familyDetails,
    bundlePrice?.Plan?.MonthlyFee,
    bundlePrice?.Plan?.RegularMonthlyFee,
  );

  const thereafterTotal = planRegularFee || planFee;

  const firstEnd = Math.min(payment.Time || 12, lowestOffer || INFINITY);
  const periods: PriceModalPeriodTotalFromBackend[] = [
    {
      periodEndMonth: firstEnd,
      monthlyTotal: discountedTotal,
      regularMonthlyTotal:
        regularTotal > discountedTotal ? regularTotal : undefined,
    },
  ];

  const showIntermediate = shouldShowIntermediate(
    intermediateTotal,
    lowestOffer,
    highestOffer,
    payment.Time,
  );

  const lowerBound =
    Math.min(lowestOffer || INFINITY, payment.Time || INFINITY) + 1;

  let upperBound: number;
  if (
    (lowestOffer || INFINITY) < (payment.Time || 12) &&
    (highestOffer || 0) > (payment.Time || 12)
  ) {
    upperBound = payment.Time || 12;
  } else if ((payment.Time || 0) < (highestOffer || 0)) {
    upperBound = Math.max(highestOffer || INFINITY, payment.Time || INFINITY);
  } else if (
    lowestOffer === highestOffer &&
    (lowestOffer || 0) >= (payment.Time || 0)
  ) {
    upperBound = Math.max(lowestOffer || 0, payment.Time || 0);
  } else {
    upperBound = Math.min(highestOffer || INFINITY, payment.Time || INFINITY);
  }

  if (showIntermediate) {
    let intermediatePeriodPrice: number;
    if (lowestOffer && lowerBound > lowestOffer) {
      intermediatePeriodPrice = intermediateTotal!;
    } else if (payment.Time < (highestOffer || INFINITY)) {
      intermediatePeriodPrice = planFee;
    } else {
      intermediatePeriodPrice = discountedTotal;
    }

    let intermediateRegPrice: number | undefined;
    if (
      payment.Time < (highestOffer || INFINITY) &&
      payment.Time <= (lowestOffer || 0)
    ) {
      intermediateRegPrice =
        planRegularFee > intermediatePeriodPrice ? planRegularFee : undefined;
    } else {
      intermediateRegPrice =
        regularTotal > intermediatePeriodPrice ? regularTotal : undefined;
    }

    periods.push({
      periodEndMonth: upperBound,
      monthlyTotal: intermediatePeriodPrice,
      regularMonthlyTotal: intermediateRegPrice,
    });
  }

  const showSecond = shouldShowSecondIntermediate(
    intermediateTotal,
    lowestOffer,
    highestOffer,
    payment.Time,
    isFamilySet,
  );

  if (showSecond) {
    const secondEnd = Math.max(payment.Time || 36, highestOffer || 36);

    let secondPrice: number;
    if (lowestOffer && lowerBound > lowestOffer) {
      if (
        payment.Time < (highestOffer || INFINITY) &&
        payment.Time >= (lowestOffer || 0)
      ) {
        secondPrice =
          familyDetails?.familyPlansTotalIntermediateMonthlyFee || 0;
      } else {
        secondPrice = regularTotal;
      }
    } else {
      secondPrice = discountedTotal;
    }

    let secondRegPrice: number | undefined;
    if (
      payment.Time < (highestOffer || INFINITY) &&
      payment.Time >= (lowestOffer || 0)
    ) {
      secondRegPrice =
        planRegularFee > secondPrice ? planRegularFee : undefined;
    } else {
      secondRegPrice = regularTotal > secondPrice ? regularTotal : undefined;
    }

    periods.push({
      periodEndMonth: secondEnd,
      monthlyTotal: secondPrice,
      regularMonthlyTotal: secondRegPrice,
    });
  }

  return { periods, thereafterTotal };
}
